import React, { useState, useMemo, KeyboardEvent, ChangeEvent } from "react";
import { ArrowUpRight, ArrowDownRight, Eye, Mail, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TopRevenueRow {
  name: string;
  type: "agent" | "brokerage";
  deal_count: number;
  average_value: number;
  revenue: number;
  change_pct: number;
  rank?: number;
  id?: string;
  agentid?: string;
  agent_id?: string;
  brokerageid?: string;
  brokerage_id?: string;
  firmId?: string;
  [key: string]: any; // allow extra fields from API
}

interface TopRevenueListProps {
  financeData?: {
    top_revenue?: TopRevenueRow[];
    [key: string]: any; // allow extra API fields
  } | null;
  baseRoute?: string;
}



const TopRevenueList: React.FC<TopRevenueListProps> = ({
  financeData,
  baseRoute = "/signage",
}) => {

  const topRevenue = financeData?.top_revenue || [];
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();

  const filteredData = useMemo(() => {
    return topRevenue.filter((item:any) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, topRevenue]);

  const formatCurrency = (val:any) =>
    `$${Number(val || 0).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables mt-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold mb-0">Top Revenue by Brokerage & Agents</h4>
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            <div className="input-group" style={{ width: "280px" }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search brokerage or agent"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setSearch(searchInput);
                  }
                }}
                style={{
                  borderRadius: "8px 0 0 8px",
                  border: "1px solid #E5E7EB",
                  fontSize: "0.9rem",
                }}
              />
              <button
                className="btn btn-primary btn-sm"
                style={{
                  borderRadius: "0 8px 8px 0",
                  fontSize: "0.9rem",
                  padding: "0 14px",
                }}
                onClick={() => setSearch(searchInput)}
              >
                <Search size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="list-group list-group-flush"
          style={{
            maxHeight: "350px",
            overflowY: "auto",
            paddingRight: "6px",
          }}>
          {filteredData.length === 0 ? (
            <div className="text-center text-muted py-4 small">
              No results found
            </div>
          ) : (
            filteredData.map((item: any, idx: number) => {
              const up = item.change_pct >= 0;
              const trendColor = up ? "#16a34a" : "#dc2626";

              return (
                <div
                  key={idx}
                  className="d-flex justify-content-between align-items-center py-3 border-bottom"
                  style={{ cursor: "default" }}
                >
                  {/* Left section */}
                  <div className="d-flex align-items-center">
                    <div
                      className="br-8 text-center fw-bold me-3"
                      style={{
                        width: "35px",
                        height: "35px",
                        backgroundColor: "#dbeafe",
                        color: "#155dfc",
                        fontSize: "1rem",
                        lineHeight: "32px",
                      }}
                    >
                      #{idx + 1}
                    </div>
                    <div>
                      <h5 className="fw-bold mb-1 text-dark">
                        {item.name}
                      </h5>
                      <div className="text-muted fw-medium">
                        {item.deal_count} deals &nbsp;·&nbsp; Avg:{" "}
                        {formatCurrency(item.average_value)}
                      </div>
                    </div>
                  </div>

                  {/* Right section */}
                  <div className="d-flex align-items-center" style={{ gap: "16px" }}>
                    <div className="align-items-center d-flex flex-column">
                      <div className="fw-bold fs-17 mb-1">
                        {formatCurrency(item.revenue)}
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-end"
                        style={{ color: trendColor }}
                      >
                        {up ? (
                          <ArrowUpRight size={14} className="me-1" />
                        ) : (
                          <ArrowDownRight size={14} className="me-1" />
                        )}
                        {Math.abs(item.change_pct).toFixed(1)}%
                      </div>
                    </div>

                    {/* Trend image placeholder */}
                    <div
                      className="rounded-3"
                      style={{
                        background:
                          "linear-gradient(180deg, #bfdbfe 0%, transparent 100%)",
                        width: "60px",
                        height: "26px",
                        position: "relative",
                      }}
                    >
                      {/* could later replace with mini sparkline */}
                    </div>

                    {/* Actions */}
                    <div className="d-flex gap-2">
                      <button
                        className="btn back-btn surface-btn gap-1 fw-bold"
                        style={{
                          border: "1px solid #6b7280",
                          color: "#0e0e0e",
                          padding: "5px 12px",
                          borderRadius: "8px",
                        }}
                        onClick={() => {
                          // Determine type from the type field
                          const itemType = item.type?.toLowerCase();
                          
                          // Get ID - check multiple possible field names
                          const itemId = item.id || item.agentid || item.agent_id || 
                                        item.brokerageid || item.brokerage_id || item.firmId;
                          
                          if (itemType === "agent" && itemId) {
                            // Navigate to agent view page
                            navigate(`${baseRoute}/view-agent/${itemId}`);
                          } else if (itemType === "brokerage" && itemId) {
                            // Navigate to brokerage view page
                            navigate(`${baseRoute}/brokerageFirm/${itemId}`);
                          } else {
                            // Fallback: try to determine from available ID fields
                            const agentId = item.agentid || item.agent_id;
                            const brokerageId = item.brokerageid || item.brokerage_id || item.firmId;
                            
                            if (agentId) {
                              navigate(`${baseRoute}/view-agent/${agentId}`);
                            } else if (brokerageId) {
                              navigate(`${baseRoute}/brokerageFirm/${brokerageId}`);
                            } else {
                              console.warn("No ID found for item:", item);
                              // Fallback to previous behavior
                              navigate(`${baseRoute}/revenue/${item.name}`);
                            }
                          }
                        }}
                      >
                        <Eye size={15} /> View
                      </button>

                      {/* <button
                        className="btn back-btn surface-btn gap-1 fw-bold"
                        style={{
                          border: "1px solid #6b7280 ",
                          color: "#0e0e0e",
                          padding: "4px 10px",
                        }}
                      >
                        <Mail size={15} /> Contact
                      </button> */}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <style >{`
        .custom-scroll::-webkit-scrollbar {
  width: 6px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 8px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: #f8fafc;
}

      `}</style>
    </div>
  );
};

export default TopRevenueList;
